import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { toast } from 'sonner';
import { format, differenceInDays, addDays } from 'date-fns';
import {
  CalendarDays,
  Plus,
  Palmtree,
  Clock,
  CheckCircle,
  XCircle,
  Calendar as CalendarIcon,
  FileText,
  AlertCircle
} from 'lucide-react';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEAVE_TYPES = [
  { value: 'casual', label: 'Casual Leave', color: 'bg-blue-100 text-blue-700' },
  { value: 'sick', label: 'Sick Leave', color: 'bg-red-100 text-red-700' },
  { value: 'earned', label: 'Earned Leave', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'unpaid', label: 'Unpaid Leave', color: 'bg-slate-100 text-slate-700' },
  { value: 'half_day', label: 'Half Day', color: 'bg-amber-100 text-amber-700' },
];

const EmployeeLeavePage = () => {
  const [user, setUser] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [leaveForm, setLeaveForm] = useState({
    leave_type: '',
    start_date: null,
    end_date: null,
    reason: ''
  });

  useEffect(() => {
    const storedUser = localStorage.getItem('employeeUser');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user, selectedYear]);

  const fetchData = async () => {
    try {
      const [leavesRes, holidaysRes] = await Promise.all([
        axios.get(`${API}/leaves`, { params: { employee_id: user.id, year: selectedYear } }),
        axios.get(`${API}/holidays`, { params: { year: selectedYear } })
      ]);
      setLeaves(leavesRes.data);
      setHolidays(holidaysRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      // Initialize with empty arrays if API doesn't exist yet
      setLeaves([]);
      setHolidays([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyLeave = async () => {
    if (!leaveForm.leave_type || !leaveForm.start_date || !leaveForm.reason) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      await axios.post(`${API}/leaves`, {
        employee_id: user.id,
        employee_name: user.name,
        leave_type: leaveForm.leave_type,
        start_date: format(leaveForm.start_date, 'yyyy-MM-dd'),
        end_date: leaveForm.end_date ? format(leaveForm.end_date, 'yyyy-MM-dd') : format(leaveForm.start_date, 'yyyy-MM-dd'),
        reason: leaveForm.reason
      });
      toast.success('Leave application submitted successfully!');
      setIsLeaveDialogOpen(false);
      resetLeaveForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error applying for leave');
    }
  };

  const resetLeaveForm = () => {
    setLeaveForm({
      leave_type: '',
      start_date: null,
      end_date: null,
      reason: ''
    });
  };

  const getLeaveTypeInfo = (type) => {
    return LEAVE_TYPES.find(t => t.value === type) || { label: type, color: 'bg-slate-100 text-slate-700' };
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
    }
  };

  const calculateLeaveBalance = () => {
    const currentMonth = new Date().getMonth() + 1; // 1-12
    const totalLeaves = currentMonth; // 1 leave per month
    const approvedLeaves = leaves.filter(l => l.status === 'approved').reduce((acc, l) => {
      const days = differenceInDays(new Date(l.end_date), new Date(l.start_date)) + 1;
      return acc + days;
    }, 0);
    return {
      total: totalLeaves,
      used: approvedLeaves,
      available: Math.max(0, totalLeaves - approvedLeaves)
    };
  };

  const leaveBalance = calculateLeaveBalance();

  const calculateDays = () => {
    if (!leaveForm.start_date) return 0;
    if (!leaveForm.end_date) return 1;
    return differenceInDays(leaveForm.end_date, leaveForm.start_date) + 1;
  };

  // Stats
  const leaveStats = {
    total: leaves.length,
    approved: leaves.filter(l => l.status === 'approved').length,
    pending: leaves.filter(l => l.status === 'pending').length,
    rejected: leaves.filter(l => l.status === 'rejected').length
  };

  const years = Array.from({ length: 3 }, (_, i) => new Date().getFullYear() - 1 + i);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="employee-leave-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-brand-navy font-heading">Leave & Holidays</h1>
          <p className="text-slate-500 mt-1">Apply for leave and view holidays</p>
        </div>
        <Button
          onClick={() => setIsLeaveDialogOpen(true)}
          data-testid="apply-leave-btn"
          className="bg-brand-navy hover:bg-brand-navy-light text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          Apply Leave
        </Button>
      </div>

      {/* Leave Balance Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-brand-navy to-brand-navy/90">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <CalendarDays className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-white/70">Total Balance</p>
                <p className="text-2xl font-bold text-white">{leaveBalance.total} days</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Available</p>
                <p className="text-2xl font-bold text-emerald-600">{leaveBalance.available} days</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Used</p>
                <p className="text-2xl font-bold text-amber-600">{leaveBalance.used} days</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Palmtree className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Bank Holidays</p>
                <p className="text-2xl font-bold text-purple-600">{holidays.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bank Holidays List */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-heading text-brand-navy flex items-center gap-2">
            <Palmtree className="h-5 w-5 text-purple-600" />
            Bank Holidays - {selectedYear}
          </CardTitle>
          <CardDescription>List of public and bank holidays</CardDescription>
        </CardHeader>
        <CardContent>
          {holidays.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {holidays.map((holiday, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-100">
                  <div className="w-12 h-12 bg-white rounded-lg flex flex-col items-center justify-center shadow-sm">
                    <span className="text-xs text-purple-600 font-medium">
                      {format(new Date(holiday.date), 'MMM')}
                    </span>
                    <span className="text-lg font-bold text-brand-navy">
                      {format(new Date(holiday.date), 'd')}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">{holiday.name}</p>
                    <p className="text-xs text-slate-500">{format(new Date(holiday.date), 'EEEE')}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <Palmtree className="h-12 w-12 mx-auto mb-2 text-slate-300" />
              <p>No holidays added for {selectedYear}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Leave Applications Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Applications</p>
                <p className="text-xl font-bold text-brand-navy">{leaveStats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Approved</p>
                <p className="text-xl font-bold text-emerald-600">{leaveStats.approved}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Pending</p>
                <p className="text-xl font-bold text-amber-600">{leaveStats.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Palmtree className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Holidays ({selectedYear})</p>
                <p className="text-xl font-bold text-purple-600">{holidays.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Holidays List */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-heading text-brand-navy flex items-center gap-2">
            <Palmtree className="h-5 w-5" />
            Public Holidays
          </CardTitle>
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {holidays.length === 0 ? (
            <div className="text-center py-8">
              <Palmtree className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No holidays listed for {selectedYear}</p>
              <p className="text-xs text-slate-400 mt-1">Holidays will be added by Admin</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {holidays.map((holiday, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-12 h-12 bg-brand-gold/20 rounded-lg flex flex-col items-center justify-center">
                    <span className="text-xs text-brand-navy">{format(new Date(holiday.date), 'MMM')}</span>
                    <span className="text-lg font-bold text-brand-navy">{format(new Date(holiday.date), 'd')}</span>
                  </div>
                  <div>
                    <p className="font-medium text-brand-navy">{holiday.name}</p>
                    <p className="text-xs text-slate-500">{format(new Date(holiday.date), 'EEEE')}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* My Leaves */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-brand-navy flex items-center gap-2">
            <CalendarDays className="h-5 w-5" />
            My Leave Applications
          </CardTitle>
        </CardHeader>
        <CardContent>
          {leaves.length === 0 ? (
            <div className="text-center py-8">
              <CalendarDays className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No leave applications yet</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => setIsLeaveDialogOpen(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Apply for Leave
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead>Days</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leaves.map((leave) => {
                  const typeInfo = getLeaveTypeInfo(leave.leave_type);
                  const days = differenceInDays(new Date(leave.end_date), new Date(leave.start_date)) + 1;
                  return (
                    <TableRow key={leave.id}>
                      <TableCell>
                        <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
                      </TableCell>
                      <TableCell>{format(new Date(leave.start_date), 'dd MMM yyyy')}</TableCell>
                      <TableCell>{format(new Date(leave.end_date), 'dd MMM yyyy')}</TableCell>
                      <TableCell>{days} day{days > 1 ? 's' : ''}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{leave.reason}</TableCell>
                      <TableCell>{getStatusBadge(leave.status)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Apply Leave Dialog */}
      <Dialog open={isLeaveDialogOpen} onOpenChange={setIsLeaveDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-navy">Apply for Leave</DialogTitle>
            <DialogDescription>Submit your leave application for approval</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Leave Type *</Label>
              <Select
                value={leaveForm.leave_type}
                onValueChange={(v) => setLeaveForm(prev => ({ ...prev, leave_type: v }))}
              >
                <SelectTrigger data-testid="leave-type-select">
                  <SelectValue placeholder="Select leave type" />
                </SelectTrigger>
                <SelectContent>
                  {LEAVE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>From Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !leaveForm.start_date && "text-muted-foreground"
                      )}
                      data-testid="leave-start-date"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {leaveForm.start_date ? format(leaveForm.start_date, 'dd MMM yyyy') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={leaveForm.start_date}
                      onSelect={(date) => setLeaveForm(prev => ({ ...prev, start_date: date }))}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>To Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !leaveForm.end_date && "text-muted-foreground"
                      )}
                      data-testid="leave-end-date"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {leaveForm.end_date ? format(leaveForm.end_date, 'dd MMM yyyy') : 'Same as start'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={leaveForm.end_date}
                      onSelect={(date) => setLeaveForm(prev => ({ ...prev, end_date: date }))}
                      disabled={(date) => leaveForm.start_date && date < leaveForm.start_date}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {leaveForm.start_date && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>Duration:</strong> {calculateDays()} day{calculateDays() > 1 ? 's' : ''}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Reason *</Label>
              <Textarea
                value={leaveForm.reason}
                onChange={(e) => setLeaveForm(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Please provide a reason for your leave..."
                data-testid="leave-reason"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsLeaveDialogOpen(false); resetLeaveForm(); }}>
              Cancel
            </Button>
            <Button
              onClick={handleApplyLeave}
              data-testid="submit-leave-btn"
              className="bg-brand-navy hover:bg-brand-navy-light"
              disabled={!leaveForm.leave_type || !leaveForm.start_date || !leaveForm.reason}
            >
              Submit Application
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeLeavePage;
